import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import checkUpReviewsApi from "../../api/checkUpReviewsApi";
import usersApi from "../../api/usersApi";
import styles from "./ReviewPage.module.css";

const STAR_VALUES = [1, 2, 3, 4, 5];
const REVIEW_QUESTIONS = [
  {
    key: "generalConsultationScore",
    text: "¿Qué te pareció la consulta en general?",
  },
  {
    key: "nutritionistCareScore",
    text: "¿La atención y trato de la nutrióloga fueron adecuados y empáticos?",
  },
  {
    key: "mealPlanExplanationScore",
    text: "¿Las explicaciones de tu plan de alimentación fueron claras y fáciles de entender?",
  },
];

function StarRating({ value, onChange, disabled }) {
  const handleClick = (event, starValue) => {
    if (disabled) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const isLeftHalf = event.clientX - rect.left <= rect.width / 2;
    onChange(isLeftHalf ? starValue - 0.5 : starValue);
  };

  return (
    <div className={styles.rating} aria-label="Calificación">
      {STAR_VALUES.map((starValue) => {
        const fill = Math.max(0, Math.min(1, value - (starValue - 1))) * 100;
        return (
          <button
            key={starValue}
            type="button"
            className={styles.starButton}
            onClick={(event) => handleClick(event, starValue)}
            disabled={disabled}
            aria-label={`${starValue} estrellas`}
          >
            <span className={styles.starGlyph} style={{ "--fill": `${fill}%` }}>★</span>
          </button>
        );
      })}
    </div>
  );
}

export default function ReviewPage() {
  const { hashId } = useParams();
  const [searchParams] = useSearchParams();
  const nutriologyId = searchParams.get("nutriologyId") || "";
  const branchId = searchParams.get("branchId") || "";

  const [nutriologyName, setNutriologyName] = useState("");
  const [ratings, setRatings] = useState({
    generalConsultationScore: 0,
    nutritionistCareScore: 0,
    mealPlanExplanationScore: 0,
  });
  const [comments, setComments] = useState("");
  const [loadingName, setLoadingName] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = useMemo(() => {
    return Boolean(
      hashId &&
      branchId &&
      nutriologyId &&
      REVIEW_QUESTIONS.every((question) => ratings[question.key] > 0)
    );
  }, [branchId, hashId, nutriologyId, ratings]);

  useEffect(() => {
    let isMounted = true;

    async function loadNutriologyName() {
      setLoadingName(true);
      try {
        const response = await usersApi.getPublicNutriologyName(nutriologyId);
        if (isMounted) setNutriologyName(response?.name || "");
      } catch (_) {
        if (isMounted) setNutriologyName("");
      } finally {
        if (isMounted) setLoadingName(false);
      }
    }

    if (nutriologyId) loadNutriologyName();
    else setLoadingName(false);

    return () => {
      isMounted = false;
    };
  }, [nutriologyId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) {
      setError("Responde las tres calificaciones para continuar.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await checkUpReviewsApi.createOrUpdate({
        hashId,
        branchId,
        nutriologyId,
        ...ratings,
        comments,
      });
      setSubmitted(true);
    } catch (submitError) {
      setError(submitError.response?.data?.message || "No se pudo enviar tu review.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className={styles.page}>
      <aside className={styles.sideBar} aria-hidden="true" />
      <section className={styles.reviewPanel}>
        <div className={styles.content}>
          <p className={styles.message}>
            Gracias por compartirnos tus comentarios de tu cita con nuestros servicios de nutriólogos,
            te atendió: <strong>{loadingName ? "..." : nutriologyName || "Nutrióloga"}</strong>
          </p>

          {submitted ? (
            <p className={styles.success}>Gracias, tus comentarios fueron registrados.</p>
          ) : (
            <form className={styles.form} onSubmit={handleSubmit}>
              <div className={styles.questions}>
                {REVIEW_QUESTIONS.map((question, index) => (
                  <fieldset className={styles.question} key={question.key}>
                    <legend>
                      <span className={styles.questionNumber}>{index + 1}.</span>
                      {question.text}
                    </legend>
                    <span className={styles.ratingHint}>1 a 5 estrellas</span>
                    <StarRating
                      value={ratings[question.key]}
                      onChange={(nextValue) => setRatings((prev) => ({ ...prev, [question.key]: nextValue }))}
                      disabled={submitting}
                    />
                  </fieldset>
                ))}
              </div>
              <textarea
                className={styles.textarea}
                placeholder="Si quieres escribir algo más, tus comentarios son apreciados (opcional)"
                value={comments}
                onChange={(event) => setComments(event.target.value)}
                disabled={submitting}
              />
              {error && <p className={styles.error}>{error}</p>}
              <button className={styles.submitButton} type="submit" disabled={!canSubmit || submitting}>
                {submitting ? "Enviando..." : "Enviar"}
              </button>
            </form>
          )}
        </div>
      </section>
      <aside className={styles.sideBar} aria-hidden="true" />
    </main>
  );
}
